import React from 'react';
import {
  View,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { SVG_COMPONENTS } from '@/assets/admin';

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface InfoBubbleBottomSheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  message: string;
}

/**
 * Bottom sheet info bubble for additional payment information
 * Used for "Other amount" info explanation
 */
export const InfoBubbleBottomSheet: React.FC<InfoBubbleBottomSheetProps> = ({
  visible,
  onClose,
  title,
  message,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const insets = useSafeAreaInsets();

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={styles.backdrop}
          onPress={onClose}
          activeOpacity={1}
        />
        
        <View style={[
          styles.bottomSheet,
          { paddingBottom: Math.max(insets.bottom, 40), backgroundColor: MARKETPLACE_COLORS.cardBackground },
        ]}>
          {/* Header with close button */}
          <View style={styles.header}>
            {title ? (
              <Text style={[styles.headerTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>{title}</Text>
            ) : (
              <View style={styles.headerSpacer} />
            )}
            <TouchableOpacity
              style={styles.closeButton}
              onPress={onClose}
              activeOpacity={0.8}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              {CloseIcon && (
                <CloseIcon width={16} height={16} color={MARKETPLACE_COLORS.textMuted} />
              )}
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={styles.content}>
            <Text style={[styles.messageText, { color: MARKETPLACE_COLORS.textPrimary }]}>{message}</Text>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  bottomSheet: {
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    paddingTop: 24,
    paddingHorizontal: 24,
    width: SCREEN_WIDTH,
    maxWidth: 375,
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 16,
  },
  headerSpacer: {
    flex: 1,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 27,
  },
  closeButton: {
    width: 20,
    height: 27,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    minHeight: 200,
  },
  messageText: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },
});
